import { SERVICE_MIN_PER_STOP } from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.mapper';

/**
 * Las opciones de atención por parada que la pantalla ofrece, y la resolución del
 * valor elegido.
 *
 * Por qué es un conjunto acotado y no un número libre: la atención es la palanca
 * más potente del plan. Medido contra el motor con 25 órdenes, capacidad no
 * restrictiva y una jornada de 540 minutos:
 *
 * ```
 *   atención    entregadas   jornada usada
 *     5 min       25/25         396/540     ← se ve perfecto, es irrealizable
 *    10 min       25/25         521/540
 *    15 min       23/25         530/540     ← el default de hoy
 *    20 min       20/25         539/540
 *    30 min       15/25         533/540
 * ```
 *
 * Cada cinco minutos cuestan entre dos y tres clientes por ruta, y **bajar el
 * número mete todo**. Un campo libre invita a bajarlo hasta que "entre todo", y el
 * resultado es un plan de buen aspecto que nadie puede ejecutar en la calle. Con
 * opciones acotadas el usuario sigue pudiendo elegir mal, pero no absurdo.
 */

/**
 * El piso operativo. Por debajo de esto el plan deja de ser optimista y pasa a ser
 * ficción: con 5 minutos por parada la jornada medida quedó en 396 de 540 y entraron
 * todas las órdenes.
 */
export const MIN_SERVICE_MIN_OPTION = 10;

/**
 * Los valores ofrecidos. Incluye el default de la pantalla —para que arrancar sin
 * tocar el control dé el resultado de siempre— y valores por encima, que son el caso
 * de uso que originó el pedido: la atención real puede ser mayor a la asumida.
 */
export const SERVICE_MIN_OPTIONS: readonly number[] = [10, 15, 20, 25, 30, 45];

/**
 * El valor a usar en la corrida.
 *
 * Cualquier cosa que no sea una de las opciones cae al default. Un número arbitrario
 * no puede haber llegado por el selector, así que llegó por un estado corrupto; el
 * motor lo aceptaría igual y produciría un plan que la pantalla no sabe explicar.
 * `0` es el caso más peligroso: es falsy y el motor lo toma, planificando una
 * operación sin tiempo de atención.
 */
export function resolveServiceMin(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return SERVICE_MIN_PER_STOP;
  return SERVICE_MIN_OPTIONS.includes(value) ? value : SERVICE_MIN_PER_STOP;
}
